import { useState } from "react";
import { Link } from "react-router-dom";
import { Zap, Instagram, Twitter, Youtube, Facebook, ArrowRight, Mail } from "lucide-react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 2400);
    setEmail("");
  };

  return (
    <footer data-testid="footer" className="relative border-t border-white/10 bg-[#0D0F12] pt-20 pb-10 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#00cccc]/40 to-transparent" />
      <div className="absolute inset-0 grid-bg opacity-5" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <a href="#" className="flex items-center gap-3 mb-5">
              <img
                src={`${process.env.PUBLIC_URL}/logo.png`}
                alt="Biker Boulevard"
                className="w-11 h-11 rounded-md object-contain"
                style={{ filter: "drop-shadow(0 0 12px rgba(0,204,204,0.4))" }}
              />
              <div className="leading-none">
                <div className="font-[Unbounded] text-base font-bold tracking-tight text-white">BIKER</div>
                <div className="font-[Unbounded] text-base font-bold tracking-tight text-[#00cccc]">BOULEVARD</div>
              </div>
            </a>
            <p className="text-sm text-zinc-400 max-w-md leading-relaxed">
              India&apos;s smartest premium pre-owned motorcycle marketplace. Built for riders who refuse to wait.
            </p>

            {/* Newsletter */}
            <form onSubmit={onSubmit} className="mt-8 max-w-md">
              <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">// Stay in the loop</label>
              <div className="mt-3 flex items-center gap-2 p-1 rounded-full glass">
                <Mail className="w-4 h-4 text-[#00cccc] ml-3" />
                <input
                  data-testid="newsletter-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1 bg-transparent px-2 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none"
                />
                <button
                  data-testid="newsletter-submit"
                  type="submit"
                  className="px-4 py-2 rounded-full bg-[#00cccc] text-black text-xs font-semibold hover:shadow-[0_0_18px_rgba(0,204,204,0.6)] transition-all flex items-center gap-1.5"
                >
                  {submitted ? "Subscribed" : "Subscribe"}
                  {!submitted && <ArrowRight className="w-3 h-3" />}
                </button>
              </div>
            </form>
          </div>

          <FooterCol title="Platform" links={["Smart Valuation", "Sell a Bike", "RevvPass", "Premium Listings"]} />
          <FooterCol title="Company" links={["About", "Press", "Careers", "Blog"]} />
          <FooterCol title="Support" links={["Help Center", "Contact", "Terms", "Privacy"]} />
        </div>

        <div className="mt-16 pt-7 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-zinc-500 font-mono">© 2026 Biker Boulevard. All rights reserved.</p>
          <div className="flex items-center gap-2">
            {[
              { Icon: Instagram, l: "Instagram", href: "https://www.instagram.com/bikerboulevard" },
              { Icon: Twitter, l: "Twitter", href: "#" },
              { Icon: Youtube, l: "YouTube", href: "#" },
              { Icon: Facebook, l: "Facebook", href: "#" },
            ].map(({ Icon, l, href }) => (
              <a
                key={l}
                href={href}
                target={href.startsWith("http") ? "_blank" : undefined}
                rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                aria-label={l}
                data-testid={`social-${l.toLowerCase()}`}
                className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-zinc-400 hover:text-[#00cccc] hover:border-[#00cccc]/40 transition-all"
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  const routes = { Privacy: "/privacy", Terms: "/terms", Contact: "/contact" };
  return (
    <div className="lg:col-span-2">
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-4">// {title}</div>
      <ul className="space-y-3">
        {links.map((l) => (
          <li key={l}>
            {routes[l] ? (
              <Link to={routes[l]} className="text-sm text-zinc-300 hover:text-[#00cccc] transition-colors" data-testid={`footer-link-${l.toLowerCase().replace(/\s+/g, "-")}`}>
                {l}
              </Link>
            ) : (
              <a href="#" className="text-sm text-zinc-300 hover:text-[#00cccc] transition-colors" data-testid={`footer-link-${l.toLowerCase().replace(/\s+/g, "-")}`}>
                {l}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
